import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Check, X, Trash2, MapPin, Bell } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { EVENT_KINDS, EVENT_STATUS, REMIND_OPTIONS, fmtDT, dayKey, toLocalInput } from "@/lib/plannerOptions";
import { EventDialog } from "@/components/planner/EventDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const kindCls = (e) => (e.status === "DONE" ? "bg-slate-200 text-slate-500 line-through" : e.kind === "APPOINTMENT" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800");

const MonthGrid = ({ month, events, selected, onSelect }) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  const byDay = useMemo(() => events.reduce((m, e) => { const k = dayKey(new Date(e.start)); (m[k] = m[k] || []).push(e); return m; }, {}), [events]);
  const today = dayKey(new Date());
  return (
    <div className="grid grid-cols-7 border-l border-t border-slate-200 rounded-lg overflow-hidden" data-testid="calendar-grid">
      {WEEKDAYS.map((w) => <div key={w} className="bg-slate-50 border-r border-b text-[11px] font-semibold uppercase text-slate-500 px-2 py-1.5">{w}</div>)}
      {days.map((d) => {
        const k = dayKey(d);
        const list = byDay[k] || [];
        return (
          <button key={k} data-testid={`calendar-day-${k}`} onClick={() => onSelect(d)}
            className={`min-h-24 border-r border-b p-1.5 text-left align-top transition-colors ${d.getMonth() !== month.getMonth() ? "bg-slate-50/60 text-slate-400" : "bg-white"} ${dayKey(selected) === k ? "ring-2 ring-inset ring-slate-900" : "hover:bg-slate-50"}`}>
            <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${k === today ? "bg-emerald-600 text-white" : ""}`}>{d.getDate()}</div>
            {list.slice(0, 3).map((e) => <div key={e.id} className={`truncate rounded px-1 py-0.5 text-[10px] mb-0.5 ${kindCls(e)}`}>{new Date(e.start).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} {e.title}</div>)}
            {list.length > 3 && <div className="text-[10px] text-slate-500">+{list.length - 3} daha</div>}
          </button>
        );
      })}
    </div>
  );
};

export const EventCard = ({ e, onEdit, onChanged }) => {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); } };
  return (
    <Card data-testid={`event-card-${e.id}`} className="p-3 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <button className="font-semibold text-sm text-left hover:underline" onClick={() => onEdit(e)}>{e.title}</button>
        <Badge variant="outline" className="text-[10px]">{EVENT_KINDS[e.kind]}</Badge>
        {e.status !== "PLANNED" && <Badge variant="outline" className="text-[10px]" data-testid={`event-status-${e.id}`}>{EVENT_STATUS[e.status]}</Badge>}
      </div>
      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
        <span>{fmtDT(e.start)}</span>
        {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
        {e.remind_minutes != null && <span className="flex items-center gap-1"><Bell className="w-3 h-3" />{REMIND_OPTIONS[e.remind_minutes] || `${e.remind_minutes} dk önce`}</span>}
        <span>Sorumlu: {e.assignee_name}</span>
      </div>
      {e.status === "PLANNED" && (
        <div className="flex gap-1 pt-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`event-done-${e.id}`} onClick={() => act(() => api.put(`/events/${e.id}/status`, { status: "DONE" }), "Tamamlandı")}><Check className="w-3 h-3 mr-1" />Tamamlandı</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`event-cancel-${e.id}`} onClick={() => act(() => api.put(`/events/${e.id}/status`, { status: "CANCELLED" }), "İptal edildi")}><X className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`event-delete-${e.id}`} onClick={() => window.confirm("Silinsin mi?") && act(() => api.delete(`/events/${e.id}`), "Silindi")}><Trash2 className="w-3 h-3 text-red-600" /></Button>
        </div>
      )}
    </Card>
  );
};

export default function CalendarPage() {
  const [month, setMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [dialog, setDialog] = useState(() => {
    if (!new URLSearchParams(window.location.search).get("new")) return null;
    const d = new Date(); d.setHours(10, 0, 0, 0);
    return { start: toLocalInput(d.toISOString()) };
  });
  const [remind, setRemind] = useState(60);

  const load = () => {
    const s = new Date(month.getFullYear(), month.getMonth() - 1, 20).toISOString();
    const e = new Date(month.getFullYear(), month.getMonth() + 1, 12).toISOString();
    api.get("/events", { params: { start: s, end: e } }).then((r) => setEvents(r.data));
    api.get("/events/upcoming", { params: { days: 14 } }).then((r) => setUpcoming(r.data));
  };
  useEffect(() => { load(); }, [month]);
  useEffect(() => { api.get("/planner/settings").then((r) => setRemind(r.data.default_remind)).catch(() => {}); }, []);

  const dayList = events.filter((e) => dayKey(new Date(e.start)) === dayKey(selected));
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const newAt = () => { const d = new Date(selected); d.setHours(10, 0, 0, 0); setDialog({ start: toLocalInput(d.toISOString()) }); };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Takvim</h1>
          <p className="text-slate-500 text-sm mt-1">Randevu ve etkinliklerinizi planlayın, müşteri veya portföyle ilişkilendirin.</p>
        </div>
        <Button data-testid="new-event-btn" className="bg-slate-900" onClick={newAt}><Plus className="w-4 h-4 mr-1" />{selected.toLocaleDateString("tr-TR")} için ekle</Button>
      </div>
      <div className="grid xl:grid-cols-[1fr_320px] gap-6 items-start">
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-lg" data-testid="calendar-month-label">{month.toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}</div>
            <div className="flex gap-1">
              <Button size="icon" variant="outline" data-testid="calendar-prev" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => { const t = new Date(); setMonth(new Date(t.getFullYear(), t.getMonth(), 1)); setSelected(t); }}>Bugün</Button>
              <Button size="icon" variant="outline" data-testid="calendar-next" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <MonthGrid month={month} events={events} selected={selected} onSelect={setSelected} />
        </Card>
        <div className="space-y-5">
          <section className="space-y-2" data-testid="calendar-day-list">
            <h2 className="text-base font-semibold">{selected.toLocaleDateString("tr-TR", { dateStyle: "full" })}</h2>
            {dayList.length === 0 ? <div className="text-sm text-slate-500">Kayıt yok</div> : dayList.map((e) => <EventCard key={e.id} e={e} onEdit={(x) => setDialog({ editing: x })} onChanged={load} />)}
          </section>
          <section className="space-y-2" data-testid="upcoming-list">
            <h2 className="text-base font-semibold">Yaklaşan randevular (14 gün)</h2>
            {upcoming.length === 0 ? <div className="text-sm text-slate-500">Yaklaşan kayıt yok</div> : upcoming.map((e) => <EventCard key={e.id} e={e} onEdit={(x) => setDialog({ editing: x })} onChanged={load} />)}
          </section>
        </div>
      </div>
      <EventDialog open={!!dialog} editing={dialog?.editing} defaultStart={dialog?.start} defaultRemind={remind} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
